import { useCallback, useEffect, useState } from 'react';
import {
  GrafanaApi,
  GrafanaUrl,
  type GrafanaDashboard,
  type PanelSection,
} from '@/features/service-dashboard/grafana/api';

type Phase = 'loading' | 'ready' | 'unavailable';
type Mode = 'panels' | 'full';

const RANGES = [
  { key: '5m', label: '5분' },
  { key: '1h', label: '1시간' },
  { key: '6h', label: '6시간' },
  { key: '24h', label: '24시간' },
  { key: '7d', label: '7일' },
];

/** 통짜 보기 높이 — 셸의 topbar(52px)와 본문 패딩(pt 18 + pb 40)을 빼야 페이지까지 스크롤되지 않는다. */
const FULL_HEIGHT = 'h-[calc(100dvh-var(--spacing-topbar)-58px)]';

const SOLO_MENU_STYLE_ID = 'nd-console-hide-solo-panel-menu';

/**
 * d-solo 패널에도 점 3개 메뉴 버튼이 그려지지만 눌러도 아무것도 열리지 않는다 — 메뉴 항목은
 * 대시보드 컨텍스트에서 만들어지는데 solo 렌더에는 그 컨텍스트가 없다. 죽은 버튼을 그대로 두면
 * 사용자에게는 고장으로 읽히므로 임베드에서만 감춘다(운영 그라파나는 건드리지 않는다).
 *
 * iframe은 콘솔 오리진으로 프록시되어 same-origin이라 문서에 스타일을 넣을 수 있다. 프록시 구성이
 * 바뀌어 교차 출처가 되면 접근이 막히는데, 그때는 버튼이 남을 뿐 화면은 그대로 성립한다.
 */
function hideSoloPanelMenu(frame: HTMLIFrameElement): void {
  try {
    const doc = frame.contentDocument;
    if (!doc || doc.getElementById(SOLO_MENU_STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = SOLO_MENU_STYLE_ID;
    style.textContent = '[data-testid^="data-testid Panel menu"]{display:none !important}';
    doc.head.append(style);
  } catch {
    // 교차 출처면 접근이 막힌다 — 감추지 못할 뿐이라 조용히 넘긴다.
  }
}

/** 패널 높이. 가독성 우선이라 그라파나 그리드보다 넉넉하게 준다. */
function panelHeight(type: string): number {
  if (type === 'stat' || type === 'bargauge') return 220;
  if (type === 'text') return 180;
  return 300;
}

function Toolbar({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="flex flex-wrap items-center gap-1">{children}</div>;
}

function Pill({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-[12px] ${
        active
          ? 'border-accent-line bg-accent-soft font-semibold text-accent'
          : 'border-line bg-surface text-muted hover:border-accent-line'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * 그라파나 대시보드 — 패널을 하나씩 싣는다(기본) 또는 대시보드를 통짜로 싣는다.
 *
 * 패널 목록·쿼리·제목은 전부 그라파나에서 온다. 콘솔이 정하는 것은 **배치와 시간 범위**뿐이다 —
 * 매니페스트에서 패널이 늘어도 여기를 고치지 않게.
 */
export function GrafanaDashboardView(): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading');
  const [dashboards, setDashboards] = useState<GrafanaDashboard[]>([]);
  const [uid, setUid] = useState<string | null>(null);
  const [sections, setSections] = useState<PanelSection[]>([]);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<Mode>('panels');
  const [range, setRange] = useState('1h');
  const [columns, setColumns] = useState(2);
  const [picked, setPicked] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      const list = await GrafanaApi.getDashboards();
      if (list.length === 0) throw new Error('대시보드 목록이 비어 있다 — 서버 설정을 확인해야 한다');
      setDashboards(list);
      const target = uid ?? list[0].uid;
      if (uid === null) setRange(target === GrafanaUrl.SEARCH_ES_UID ? '6h' : '1h');
      setUid(target);
      if (!(await GrafanaApi.probe())) throw new Error('그라파나에 도달하지 못했다');
      setSections(await GrafanaApi.getPanelSections(target));
      setPhase('ready');
    } catch (error) {
      setReason(error instanceof Error ? error.message : String(error));
      setPhase('unavailable');
    }
  }, [uid]);

  useEffect(() => {
    void load();
  }, [load]);

  if (phase === 'unavailable') {
    return (
      <section className="rounded-lg border border-rose-200 bg-rose-50 p-4">
        <h2 className="text-[13px] font-semibold text-rose-700">그라파나 대시보드를 불러오지 못했다</h2>
        <p className="mt-1 text-[12px] text-rose-700">{reason}</p>
        <p className="mt-1 text-[12px] text-muted">지표 수집 자체와는 무관하다 — 콘솔의 다른 메뉴는 정상이다.</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-3 rounded-md border border-rose-300 bg-white px-2 py-1 text-[12px] text-rose-700 hover:bg-rose-100"
        >
          다시 시도
        </button>
      </section>
    );
  }

  // 섹션 이름은 대시보드마다 다르므로, 대시보드가 바뀌면 선택을 놓는다.
  const selectDashboard = (next: string) => {
    if (next === uid) return;
    setPicked(null);
    setSections([]);
    setRange(next === GrafanaUrl.SEARCH_ES_UID ? '6h' : '1h');
    setUid(next);
  };

  const sectionKey = (section: PanelSection, index: number) => section.title || `개요-${index}`;
  const sectionLabel = (section: PanelSection) => section.title || '개요';
  const shown = picked === null ? sections : sections.filter((s, i) => sectionKey(s, i) === picked);

  const toolbar = (
    <div className="flex flex-col gap-1.5">
      <Toolbar>
        {dashboards.map((dashboard) => (
          <Pill key={dashboard.uid} active={dashboard.uid === uid} onClick={() => selectDashboard(dashboard.uid)}>
            {dashboard.name}
          </Pill>
        ))}
        <span className="ml-auto flex items-center gap-1">
          <Pill active={mode === 'panels'} onClick={() => setMode('panels')}>
            패널별
          </Pill>
          <Pill active={mode === 'full'} onClick={() => setMode('full')}>
            대시보드 통짜
          </Pill>
          {uid && (
            // same-origin 경로다 — 새 탭에서 열어도 프런트가 그라파나 호스트를 알지 못한다.
            // 콘솔 셸 밖(풀페이지)이라 GrafanaUrl.dashboardSrc의 theme=light를 강제하지 않는다.
            <a
              href={GrafanaUrl.dashboardHref(uid)}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-line bg-surface px-2 py-1 text-[12px] text-muted hover:border-accent-line"
            >
              새 탭 ↗
            </a>
          )}
        </span>
      </Toolbar>

      {mode === 'panels' && sections.length > 1 && (
        <Toolbar>
          <span className="text-[11px] text-faint">구획</span>
          <Pill active={picked === null} onClick={() => setPicked(null)}>
            전체
          </Pill>
          {sections.map((section, index) => (
            <Pill
              key={sectionKey(section, index)}
              active={picked === sectionKey(section, index)}
              onClick={() => setPicked(sectionKey(section, index))}
            >
              {sectionLabel(section)}
              <span className="ml-1 font-normal text-faint">{section.panels.length}</span>
            </Pill>
          ))}
        </Toolbar>
      )}

      {mode === 'panels' && (
        <Toolbar>
          <span className="text-[11px] text-faint">구간</span>
          {RANGES.map((item) => (
            <Pill key={item.key} active={range === item.key} onClick={() => setRange(item.key)}>
              {item.label}
            </Pill>
          ))}
          <span className="ml-3 text-[11px] text-faint">열</span>
          {[1, 2, 3].map((count) => (
            <Pill key={count} active={columns === count} onClick={() => setColumns(count)}>
              {count}
            </Pill>
          ))}
        </Toolbar>
      )}
    </div>
  );

  if (mode === 'full') {
    return (
      <section className={`flex ${FULL_HEIGHT} min-h-0 flex-col gap-2`}>
        {toolbar}
        {uid && (
          <iframe
            key={uid}
            title="Grafana 대시보드"
            src={GrafanaUrl.dashboardSrc(uid)}
            className="w-full min-h-0 flex-1 rounded-xl border border-line bg-surface shadow-sm"
          />
        )}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      {toolbar}

      {phase === 'loading' && <div className="text-[12px] text-muted">불러오는 중…</div>}

      {shown.map((section) => {
        const index = sections.indexOf(section);
        const key = sectionKey(section, index);
        return (
          <div key={key} className="flex flex-col gap-2.5">
            {picked === null && (
              <div className="flex items-center gap-2 border-b border-line-soft pb-1.5 text-[13px] font-bold text-ink">
                {sectionLabel(section)}
                <span className="text-[11px] font-normal text-faint">패널 {section.panels.length}</span>
              </div>
            )}
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {section.panels.map((panel) => (
                <div
                  key={`${uid}-${panel.id}-${range}`}
                  // 그라파나 그리드 폭이 24(전폭)인 패널은 콘솔에서도 한 줄을 다 쓴다.
                  className={`relative rounded-xl border border-line bg-surface shadow-sm ${panel.width >= 24 ? 'col-span-full' : ''}`}
                >
                  <iframe
                    title={panel.title || `패널 ${panel.id}`}
                    src={uid ? GrafanaUrl.panelSrc(uid, panel.id, range) : ''}
                    onLoad={(event) => hideSoloPanelMenu(event.currentTarget)}
                    style={{ height: panelHeight(panel.type) }}
                    className="w-full rounded-xl"
                  />
                  {uid && (
                    <a
                      href={GrafanaUrl.panelHref(uid, panel.id, range)}
                      target="_blank"
                      rel="noreferrer"
                      title="그라파나에서 크게 보기"
                      aria-label={`${panel.title || `패널 ${panel.id}`} 크게 보기`}
                      className="absolute right-2 top-2 rounded-md border border-line bg-surface px-1.5 py-0.5 text-[11px] leading-none text-faint hover:text-ink"
                    >
                      ↗
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}
